import { Controller } from "@hotwired/stimulus";

/**
 * API Unification Controller
 *
 * Handles the interactive example switcher for the API Unification section.
 * Shows 3 examples: Size Normalization (with animated flowchart showing parallel requests + transformation),
 * Async-to-Sync, and Format Flexibility.
 */
export default class extends Controller<HTMLElement> {
  static targets = [
    "tab",
    "panel",
    "packet1",
    "packet1Inner",
    "packet2",
    "packet2Inner",
    "pathToLumenfall",
    "pathToProvider1",
    "pathToProvider2",
    "lumenfallNode",
    "provider1Node",
    "provider2Node",
    "provider1Status",
    "provider2Status",
    "provider1ParamLabel",
    "provider2ParamLabel",
    "connectionLine",
    "animationContainer",
    "appNode",
  ];

  declare readonly tabTargets: HTMLElement[];
  declare readonly panelTargets: HTMLElement[];
  declare readonly packet1Target: SVGGElement;
  declare readonly packet1InnerTarget: SVGGElement;
  declare readonly packet2Target: SVGGElement;
  declare readonly packet2InnerTarget: SVGGElement;
  declare readonly pathToLumenfallTarget: SVGPathElement;
  declare readonly pathToProvider1Target: SVGPathElement;
  declare readonly pathToProvider2Target: SVGPathElement;
  declare readonly lumenfallNodeTarget: SVGGElement;
  declare readonly provider1NodeTarget: SVGGElement;
  declare readonly provider2NodeTarget: SVGGElement;
  declare readonly provider1StatusTarget: SVGGElement;
  declare readonly provider2StatusTarget: SVGGElement;
  declare readonly provider1ParamLabelTarget: SVGGElement;
  declare readonly provider2ParamLabelTarget: SVGGElement;
  declare readonly connectionLineTargets: SVGPathElement[];
  declare readonly animationContainerTarget: HTMLElement;
  declare readonly appNodeTarget: SVGGElement;

  declare readonly hasPacket1Target: boolean;
  declare readonly hasAnimationContainerTarget: boolean;

  private currentExample: string = "size";
  private isAnimating: boolean = false;
  private animationTimeout: ReturnType<typeof setTimeout> | null = null;
  private observer: IntersectionObserver | null = null;
  private themeObserver: MutationObserver | null = null;

  connect(): void {
    this.initializeColors();
    this.setupThemeObserver();
    this.setupIntersectionObserver();
    this.showPanel("size", false);
  }

  disconnect(): void {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  switchExample(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const example = target.dataset.example;

    if (!example || example === this.currentExample) return;

    this.showPanel(example, true);
  }

  private showPanel(example: string, animate: boolean): void {
    // Update tabs
    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.example === example;
      if (isActive) {
        tab.classList.add("bg-base-content", "text-base-100", "shadow-lg");
        tab.classList.remove("text-base-content/60", "hover:text-base-content", "hover:bg-base-content/10");
      } else {
        tab.classList.remove("bg-base-content", "text-base-100", "shadow-lg");
        tab.classList.add("text-base-content/60", "hover:text-base-content", "hover:bg-base-content/10");
      }
    });

    // Update panels
    this.panelTargets.forEach((panel) => {
      const isActive = panel.dataset.example === example;
      if (isActive) {
        panel.classList.remove("hidden");
        if (animate) {
          panel.classList.add("animate-fade-in");
        }
      } else {
        panel.classList.add("hidden");
        panel.classList.remove("animate-fade-in");
      }
    });

    this.currentExample = example;

    // Restart animation for size example
    if (this.hasPacket1Target && example === "size") {
      if (this.animationTimeout) {
        clearTimeout(this.animationTimeout);
      }
      this.isAnimating = false;
      this.startAnimation();
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isAnimating && this.currentExample === "size") {
            this.startAnimation();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this.element);
  }

  private async startAnimation(): Promise<void> {
    if (this.isAnimating || !this.hasPacket1Target || this.currentExample !== "size") return;
    this.isAnimating = true;

    this.resetAnimation();
    await this.pause(600);

    // Phase 1: Show packet at starting position and animate to Lumenfall
    this.showPacket(this.packet1Target, this.pathToLumenfallTarget);
    await this.animateAlongPath(this.packet1Target, this.pathToLumenfallTarget, 400);

    // Dot enters: fade out packet and light up Lumenfall simultaneously
    this.fadeOutPacket(this.packet1Target);
    this.lightUpLumenfallNode();
    await this.pause(250);

    // Brief pause in Lumenfall (processing/transforming)
    await this.pause(200);

    // Phase 2: Two packets emerge simultaneously from Lumenfall
    // Position both packets at the start of their respective paths while hidden
    const provider1StartPoint = this.pathToProvider1Target.getPointAtLength(0);
    const provider2StartPoint = this.pathToProvider2Target.getPointAtLength(0);

    this.packet1Target.setAttribute("transform", `translate(${provider1StartPoint.x}, ${provider1StartPoint.y})`);
    this.packet2Target.setAttribute("transform", `translate(${provider2StartPoint.x}, ${provider2StartPoint.y})`);

    // Show parameter labels
    this.showParamLabel(this.provider1ParamLabelTarget);
    this.showParamLabel(this.provider2ParamLabelTarget);

    // Dots exit: fade in both packets and dim Lumenfall simultaneously
    this.fadeInPacket(this.packet1Target);
    this.fadeInPacket(this.packet2Target);
    this.dimLumenfallNode();
    await this.pause(250);

    // Animate both packets simultaneously, fading out before reaching boxes
    await Promise.all([
      this.animateAlongPathAndFadeOut(this.packet1Target, this.pathToProvider1Target, 400, 0.85),
      this.animateAlongPathAndFadeOut(this.packet2Target, this.pathToProvider2Target, 400, 0.85),
    ]);

    // Show success on both providers simultaneously after packets have faded
    await Promise.all([
      this.showProviderSuccess(this.provider1NodeTarget, this.provider1StatusTarget),
      this.showProviderSuccess(this.provider2NodeTarget, this.provider2StatusTarget),
    ]);

    // Wait and loop
    await this.pause(2500);

    this.isAnimating = false;

    // Loop the animation
    this.animationTimeout = setTimeout(() => {
      if (this.currentExample === "size") {
        this.startAnimation();
      }
    }, 800);
  }

  private resetAnimation(): void {
    if (!this.hasPacket1Target) return;

    // Reset packets
    this.packet1Target.style.opacity = "0";
    this.packet1Target.setAttribute("transform", "translate(-50, -50)");
    this.packet2Target.style.opacity = "0";
    this.packet2Target.setAttribute("transform", "translate(-50, -50)");

    // Reset Lumenfall node
    const lumenfallRect = this.lumenfallNodeTarget?.querySelector("rect");
    if (lumenfallRect) {
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.15)");
      lumenfallRect.setAttribute("stroke-width", "2");
      lumenfallRect.style.transition = "";
    }

    // Reset provider nodes
    const provider1Rect = this.provider1NodeTarget?.querySelector("rect");
    if (provider1Rect) {
      const fill = this.getNodeFillColor();
      const stroke = this.getNodeStrokeColor();
      provider1Rect.setAttribute("fill", fill);
      provider1Rect.setAttribute("stroke", stroke);
      provider1Rect.setAttribute("stroke-width", "1.5");
      provider1Rect.style.transition = "";
    }

    const provider2Rect = this.provider2NodeTarget?.querySelector("rect");
    if (provider2Rect) {
      const fill = this.getNodeFillColor();
      const stroke = this.getNodeStrokeColor();
      provider2Rect.setAttribute("fill", fill);
      provider2Rect.setAttribute("stroke", stroke);
      provider2Rect.setAttribute("stroke-width", "1.5");
      provider2Rect.style.transition = "";
    }

    // Hide status indicators
    this.provider1StatusTarget?.classList.remove("opacity-100");
    this.provider1StatusTarget?.classList.add("opacity-0");
    this.provider2StatusTarget?.classList.remove("opacity-100");
    this.provider2StatusTarget?.classList.add("opacity-0");

    // Hide param labels
    if (this.provider1ParamLabelTarget) {
      this.provider1ParamLabelTarget.style.opacity = "0";
      this.provider1ParamLabelTarget.style.transition = "";
    }
    if (this.provider2ParamLabelTarget) {
      this.provider2ParamLabelTarget.style.opacity = "0";
      this.provider2ParamLabelTarget.style.transition = "";
    }
  }

  private showPacket(packet: SVGGElement, path: SVGPathElement): void {
    const startPoint = path.getPointAtLength(0);
    packet.setAttribute("transform", `translate(${startPoint.x}, ${startPoint.y})`);
    packet.style.transition = "none";
    packet.style.opacity = "1";
  }

  private fadeOutPacket(packet: SVGGElement): void {
    packet.style.transition = "opacity 200ms ease-out";
    packet.style.opacity = "0";
  }

  private fadeInPacket(packet: SVGGElement): void {
    packet.style.transition = "opacity 200ms ease-in";
    packet.style.opacity = "1";
  }

  private lightUpLumenfallNode(): void {
    const lumenfallRect = this.lumenfallNodeTarget?.querySelector("rect");
    if (lumenfallRect) {
      lumenfallRect.style.transition = "fill 200ms ease-out, stroke-width 200ms ease-out";
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.35)");
      lumenfallRect.setAttribute("stroke-width", "3");
    }
  }

  private dimLumenfallNode(): void {
    const lumenfallRect = this.lumenfallNodeTarget?.querySelector("rect");
    if (lumenfallRect) {
      lumenfallRect.style.transition = "fill 200ms ease-in, stroke-width 200ms ease-in";
      lumenfallRect.setAttribute("fill", "oklch(93.0% 0.147 126.0 / 0.15)");
      lumenfallRect.setAttribute("stroke-width", "2");
    }
  }

  private showParamLabel(labelElement: SVGGElement): void {
    if (labelElement) {
      labelElement.style.transition = "opacity 300ms ease-out";
      labelElement.style.opacity = "1";
    }
  }

  private async showProviderSuccess(nodeTarget: SVGGElement, statusTarget: SVGGElement): Promise<void> {
    const isDark = this.isDarkMode();

    // Highlight node with success using theme primary color (lime/chartreuse)
    // This creates visual continuity with the packets and Lumenfall branding
    const rect = nodeTarget?.querySelector("rect");
    if (rect) {
      rect.style.transition = "fill 200ms ease-out, stroke 200ms ease-out";
      // Use primary color with adjusted opacity for fill, full primary for stroke
      rect.setAttribute("fill", isDark ? "oklch(93.0% 0.147 126.0 / 0.2)" : "oklch(93.0% 0.147 126.0 / 0.35)");
      rect.setAttribute("stroke", "oklch(93.0% 0.147 126.0)");
      rect.setAttribute("stroke-width", "2");
    }

    // Show success status
    statusTarget?.classList.remove("opacity-0");
    statusTarget?.classList.add("opacity-100");

    await this.pause(100);
  }

  private animateAlongPath(packet: SVGGElement, path: SVGPathElement, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const length = path.getTotalLength();
      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = this.easeInOutCubic(progress);
        const point = path.getPointAtLength(easeProgress * length);

        packet.setAttribute("transform", `translate(${point.x}, ${point.y})`);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  private animateAlongPathAndFadeOut(
    packet: SVGGElement,
    path: SVGPathElement,
    duration: number,
    fadeOutAt: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const length = path.getTotalLength();
      let startTime: number | null = null;
      let hasFadedOut = false;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = this.easeInOutCubic(progress);
        const point = path.getPointAtLength(easeProgress * length);

        packet.setAttribute("transform", `translate(${point.x}, ${point.y})`);

        // Start fading out when we reach the fadeOutAt threshold
        if (progress >= fadeOutAt && !hasFadedOut) {
          hasFadedOut = true;
          this.fadeOutPacket(packet);
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private isDarkMode(): boolean {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme) {
      return dataTheme === "lumenfall-dark";
    }
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  }

  private getNodeFillColor(): string {
    return this.isDarkMode() ? "oklch(30% 0.01 90 / 0.5)" : "oklch(88.7% 0.01 90 / 0.5)";
  }

  private getNodeStrokeColor(): string {
    return this.isDarkMode() ? "oklch(50% 0.01 89 / 0.3)" : "oklch(34% 0.01 89 / 0.3)";
  }

  private getLineColor(): string {
    return this.isDarkMode() ? "oklch(70% 0.01 89 / 0.35)" : "oklch(34% 0.01 89 / 0.25)";
  }

  private initializeColors(): void {
    if (!this.hasPacket1Target) return;

    const nodeFill = this.getNodeFillColor();
    const nodeStroke = this.getNodeStrokeColor();
    const lineColor = this.getLineColor();

    // App node
    const appRect = this.appNodeTarget?.querySelector("rect");
    if (appRect) {
      appRect.setAttribute("fill", nodeFill);
      appRect.setAttribute("stroke", nodeStroke);
    }

    // Provider 1 node
    const provider1Rect = this.provider1NodeTarget?.querySelector("rect");
    if (provider1Rect) {
      provider1Rect.setAttribute("fill", nodeFill);
      provider1Rect.setAttribute("stroke", nodeStroke);
    }

    // Provider 2 node
    const provider2Rect = this.provider2NodeTarget?.querySelector("rect");
    if (provider2Rect) {
      provider2Rect.setAttribute("fill", nodeFill);
      provider2Rect.setAttribute("stroke", nodeStroke);
    }

    // Connection lines
    this.connectionLineTargets.forEach((line) => {
      line.setAttribute("stroke", lineColor);
    });
  }

  private setupThemeObserver(): void {
    this.themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "data-theme") {
          this.initializeColors();
        }
      }
    });

    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }
}
